import { ChevronDown } from 'lucide-react';

import type { FaixaRascunho } from '@/features/admin/catalogo-exames/rascunho';
import { cabeNaCelula } from '@/features/admin/catalogo-exames/rascunho';
import { descreverPublico } from '@/features/admin/catalogo-exames/rotulos';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface Props {
  faixas: FaixaRascunho[];
  unidade: string;
  somenteLeitura: boolean;
  /** Edita a faixa simples da célula (cria uma se ainda não existir). */
  aoMudar: (campo: 'minimo' | 'maximo' | 'observacao', valor: string) => void;
  /** Abre o diálogo completo — o caminho das faixas por sexo/idade. */
  aoAbrirDetalhe: () => void;
  detalheBloqueado: boolean;
}

/**
 * Uma célula da grade: a faixa daquele analito para aquela espécie.
 *
 * Três estados, e a distinção é o ponto:
 *  · nenhuma faixa ou UMA simples → mín, máx e observação editáveis aqui;
 *  · faixa qualitativa ou por sexo/idade → resumo + botão para o diálogo;
 *  · mais de uma faixa → contador + botão.
 *
 * Espremer sexo e idade na célula esconderia justamente o que faz aquela faixa
 * ser diferente das outras — e o admin escolheria a errada sem perceber.
 */
export function CelulaDeFaixa({
  faixas,
  unidade,
  somenteLeitura,
  aoMudar,
  aoAbrirDetalhe,
  detalheBloqueado,
}: Props) {
  const simples = faixas.length <= 1 && (faixas[0] === undefined || cabeNaCelula(faixas[0]));

  if (!simples) {
    const rotulo =
      faixas.length === 1 ? descreverPublico(faixas[0] as never) : `${faixas.length} faixas`;

    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-between font-normal"
        onClick={aoAbrirDetalhe}
        disabled={detalheBloqueado}
        title={
          detalheBloqueado
            ? 'Salve as alterações da grade antes de abrir o detalhe da faixa.'
            : undefined
        }
      >
        <span className="truncate">{rotulo}</span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0" />
      </Button>
    );
  }

  const faixa = faixas[0];

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Input
          aria-label="Mínimo"
          placeholder="mín"
          inputMode="decimal"
          className="h-8 text-sm"
          disabled={somenteLeitura}
          value={faixa?.minimo ?? ''}
          onChange={(e) => aoMudar('minimo', e.target.value)}
        />
        <span className="text-muted-foreground text-xs">–</span>
        <Input
          aria-label="Máximo"
          placeholder="máx"
          inputMode="decimal"
          className="h-8 text-sm"
          disabled={somenteLeitura}
          value={faixa?.maximo ?? ''}
          onChange={(e) => aoMudar('maximo', e.target.value)}
        />
        {unidade && <span className="text-xs text-muted-foreground shrink-0">{unidade}</span>}
      </div>

      {/* A observação é a ressalva que o veterinário lê junto do valor ("fêmea
          gestante", "jejum de 12h"). Estava só no diálogo, que fica bloqueado
          enquanto a grade tem alteração pendente — ou seja, indisponível
          justamente na hora de preencher. Inline, como era na V1. */}
      <Input
        aria-label="Observação"
        placeholder="Ex.: fêmea gestante"
        className="h-8 text-xs"
        disabled={somenteLeitura}
        value={faixa?.observacao ?? ''}
        onChange={(e) => aoMudar('observacao', e.target.value)}
      />
    </div>
  );
}
